import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import { User, CreditCard, DownloadSimple, Trash, Warning, Crown, Rocket, Star, NotionLogo, CheckCircle, ArrowSquareOut, Plug, Cards as TrelloIcon, CheckSquare, Translate } from '@phosphor-icons/react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  free: { name: 'Starter', icon: Star, color: '#9CA3AF' },
  pro_trial: { name: 'Pro Trial', icon: Rocket, color: '#10B981' },
  pro: { name: 'Pro', icon: Rocket, color: '#2563EB' },
  agency: { name: 'Agency', icon: Crown, color: '#F59E0B' },
};

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 bg-[#2563EB]/20 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon size={20} weight="bold" className="text-[#2563EB]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-[Outfit] font-bold">{title}</h3>
        {description && <p className="text-sm text-white/60 mt-1">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Settings = ({ user }) => {
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const [plan, setPlan] = useState('free');
  const [portalLoading, setPortalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Notion integration
  const [notion, setNotion] = useState({ configured: false, parent_page_id: '', updated_at: null });
  const [notionToken, setNotionToken] = useState('');
  const [notionPageId, setNotionPageId] = useState('');
  const [notionSaving, setNotionSaving] = useState(false);
  const [notionDisconnecting, setNotionDisconnecting] = useState(false);
  // Trello integration
  const [trello, setTrello] = useState({ configured: false, default_list_id: '', board_name: '', list_name: '', updated_at: null });
  const [trelloApiKey, setTrelloApiKey] = useState('');
  const [trelloApiToken, setTrelloApiToken] = useState('');
  const [trelloListId, setTrelloListId] = useState('');
  const [trelloSaving, setTrelloSaving] = useState(false);
  const [trelloDisconnecting, setTrelloDisconnecting] = useState(false);
  // ClickUp integration
  const [clickup, setClickup] = useState({ configured: false, default_list_id: '', list_name: '', workspace_name: '', updated_at: null });
  const [clickupToken, setClickupToken] = useState('');
  const [clickupListId, setClickupListId] = useState('');
  const [clickupSaving, setClickupSaving] = useState(false);
  const [clickupDisconnecting, setClickupDisconnecting] = useState(false);

  const loadPlan = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/me`, { withCredentials: true });
      setPlan(response.data.plan);
    } catch {
      // Stay on default
    }
  }, []);

  const loadNotion = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notion/settings`, { withCredentials: true });
      setNotion({
        configured: !!data?.configured,
        parent_page_id: data?.parent_page_id || '',
        updated_at: data?.updated_at || null,
      });
      if (data?.parent_page_id) setNotionPageId(data.parent_page_id);
    } catch {
      setNotion({ configured: false, parent_page_id: '', updated_at: null });
    }
  }, []);

  const loadTrello = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/trello/settings`, { withCredentials: true });
      setTrello({
        configured: !!data?.configured,
        default_list_id: data?.default_list_id || '',
        board_name: data?.board_name || '',
        list_name: data?.list_name || '',
        updated_at: data?.updated_at || null,
      });
      if (data?.default_list_id) setTrelloListId(data.default_list_id);
    } catch {
      setTrello({ configured: false, default_list_id: '', board_name: '', list_name: '', updated_at: null });
    }
  }, []);

  const loadClickup = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clickup/settings`, { withCredentials: true });
      setClickup({
        configured: !!data?.configured,
        default_list_id: data?.default_list_id || '',
        list_name: data?.list_name || '',
        workspace_name: data?.workspace_name || '',
        updated_at: data?.updated_at || null,
      });
      if (data?.default_list_id) setClickupListId(data.default_list_id);
    } catch {
      setClickup({ configured: false, default_list_id: '', list_name: '', workspace_name: '', updated_at: null });
    }
  }, []);

  useEffect(() => {
    loadPlan();
    loadNotion();
    loadTrello();
    loadClickup();
  }, [loadPlan, loadNotion, loadTrello, loadClickup]);

  // Scroll to #notion or #trello anchor when redirected from an export button
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleManageSubscription = useCallback(async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/customer-portal`,
        { return_url: `${window.location.origin}/settings` },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        toast.error(tr('settings.no_active_sub'));
      } else {
        toast.error(tr('settings.portal_not_configured'));
      }
      setPortalLoading(false);
    }
  }, [tr]);

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/account/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `funnelhack-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(tr('settings.export_ok'));
    } catch {
      toast.error(tr('settings.export_fail'));
    } finally {
      setExportLoading(false);
    }
  }, [tr]);

  const handleNotionSave = useCallback(async () => {
    const tokenTrim = notionToken.trim();
    const pageTrim = notionPageId.trim();
    if (!tokenTrim || !pageTrim) {
      toast.error(tr('settings.notion_fill'));
      return;
    }
    setNotionSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/notion/settings`,
        { token: tokenTrim, parent_page_id: pageTrim },
        { withCredentials: true }
      );
      setNotion({
        configured: !!data.configured,
        parent_page_id: data.parent_page_id,
        updated_at: data.updated_at,
      });
      setNotionToken('');
      setNotionPageId(data.parent_page_id || pageTrim);
      toast.success(tr('settings.notion_connected'));
    } catch (err) {
      const detail = err.response?.data?.detail || tr('settings.notion_save_fail');
      toast.error(detail);
    } finally {
      setNotionSaving(false);
    }
  }, [notionToken, notionPageId, tr]);

  const handleNotionDisconnect = useCallback(async () => {
    setNotionDisconnecting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/notion/settings`, { withCredentials: true });
      setNotion({ configured: false, parent_page_id: '', updated_at: null });
      setNotionToken('');
      setNotionPageId('');
      toast.success(tr('settings.notion_disconnected'));
    } catch {
      toast.error(tr('settings.disconnect_fail'));
    } finally {
      setNotionDisconnecting(false);
    }
  }, [tr]);

  const handleTrelloSave = useCallback(async () => {
    const k = trelloApiKey.trim();
    const t = trelloApiToken.trim();
    const l = trelloListId.trim();
    if (!k || !t || !l) {
      toast.error(tr('settings.trello_fill'));
      return;
    }
    setTrelloSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/trello/settings`,
        { api_key: k, api_token: t, default_list_id: l },
        { withCredentials: true }
      );
      setTrello({
        configured: !!data.configured,
        default_list_id: data.default_list_id,
        board_name: data.board_name || '',
        list_name: data.list_name || '',
        updated_at: data.updated_at,
      });
      setTrelloApiKey('');
      setTrelloApiToken('');
      setTrelloListId(data.default_list_id || l);
      toast.success(`${tr('settings.trello_connected')}${data.board_name ? ` — ${data.board_name}` : ''}!`);
    } catch (err) {
      const detail = err.response?.data?.detail || tr('settings.trello_save_fail');
      toast.error(typeof detail === 'string' ? detail : tr('settings.trello_invalid'));
    } finally {
      setTrelloSaving(false);
    }
  }, [trelloApiKey, trelloApiToken, trelloListId, tr]);

  const handleTrelloDisconnect = useCallback(async () => {
    setTrelloDisconnecting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/trello/settings`, { withCredentials: true });
      setTrello({ configured: false, default_list_id: '', board_name: '', list_name: '', updated_at: null });
      setTrelloApiKey('');
      setTrelloApiToken('');
      setTrelloListId('');
      toast.success(tr('settings.trello_disconnected'));
    } catch {
      toast.error(tr('settings.disconnect_fail'));
    } finally {
      setTrelloDisconnecting(false);
    }
  }, [tr]);

  const handleClickupSave = useCallback(async () => {
    const t = clickupToken.trim();
    const l = clickupListId.trim();
    if (!t || !l) {
      toast.error(tr('settings.clickup_fill'));
      return;
    }
    setClickupSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/clickup/settings`,
        { api_token: t, default_list_id: l },
        { withCredentials: true }
      );
      setClickup({
        configured: !!data.configured,
        default_list_id: data.default_list_id,
        list_name: data.list_name || '',
        workspace_name: data.workspace_name || '',
        updated_at: data.updated_at,
      });
      setClickupToken('');
      setClickupListId(data.default_list_id || l);
      toast.success(`ClickUp conectado${data.list_name ? ` — lista "${data.list_name}"` : ''}!`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Falha a guardar — verifica o token.';
      toast.error(typeof detail === 'string' ? detail : 'Token inválido.');
    } finally {
      setClickupSaving(false);
    }
  }, [clickupToken, clickupListId, tr]);

  const handleClickupDisconnect = useCallback(async () => {
    setClickupDisconnecting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/clickup/settings`, { withCredentials: true });
      setClickup({ configured: false, default_list_id: '', list_name: '', workspace_name: '', updated_at: null });
      setClickupToken('');
      setClickupListId('');
      toast.success(tr('settings.clickup_disconnected'));
    } catch {
      toast.error(tr('settings.disconnect_fail'));
    } finally {
      setClickupDisconnecting(false);
    }
  }, [tr]);

  const handleDeleteAccount = useCallback(async () => {
    setDeleting(true);
    try {
      // Confirmação server-side: o email exato da conta tem de acompanhar o pedido
      const me = await axios.get(`${BACKEND_URL}/api/account/me`, { withCredentials: true });
      await axios.delete(`${BACKEND_URL}/api/account`, {
        withCredentials: true,
        data: { confirm_email: me.data?.email || '' },
      });
      try { localStorage.removeItem('session_token'); } catch { /* noop */ }
      toast.success(tr('settings.account_deleted'));
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || tr('settings.account_delete_fail'));
      setDeleting(false);
    }
  }, [navigate, tr]);

  const planMeta = PLAN_META[plan] || PLAN_META.free;
  const PlanIcon = planMeta.icon;
  const isPaid = plan === 'pro' || plan === 'agency';

  return (
    <DashboardLayout user={user} activePage="/settings">
      <div className="p-6 max-w-3xl" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">{tr('settings.title')}</h1>
          <p className="text-white/60">{tr('settings.subtitle')}</p>
        </div>

        <div className="space-y-4">
          {/* Language */}
          <div id="language" data-testid="language-section">
            <SectionCard
              icon={Translate}
              title={tr('settings.section_language')}
              description={tr('settings.section_language_desc')}
            >
              <LanguageSwitcher variant="inline" />
            </SectionCard>
          </div>

          {/* Account info */}
          <SectionCard icon={User} title={tr('settings.section_account')} description={tr('settings.section_account_desc')}>
            <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg">
              <img
                src={user?.picture || 'https://via.placeholder.com/64'}
                alt={user?.name}
                className="w-16 h-16 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-white/60 truncate">{user?.email}</p>
              </div>
            </div>
          </SectionCard>

          {/* Subscription */}
          <SectionCard icon={CreditCard} title={tr('settings.section_plan')} description={tr('settings.section_plan_desc')}>
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg mb-4">
              <div className="flex items-center gap-3">
                <PlanIcon size={24} weight="bold" style={{ color: planMeta.color }} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40">Plano actual</p>
                  <p className="text-xl font-[Outfit] font-bold" style={{ color: planMeta.color }}>
                    {planMeta.name}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => navigate('/pricing')}
                data-testid="view-plans-btn"
                className="px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg text-sm font-medium transition-all"
              >
                {isPaid ? 'Trocar de plano' : 'Ver planos'}
              </button>
              {isPaid && (
                <button
                  onClick={handleManageSubscription}
                  data-testid="manage-subscription-btn"
                  disabled={portalLoading}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                >
                  {portalLoading ? 'A abrir...' : 'Gerenciar / Cancelar'}
                </button>
              )}
            </div>
          </SectionCard>

          {/* Notion integration */}
          <div id="notion" data-testid="notion-section">
            <SectionCard
              icon={NotionLogo}
              title={tr('settings.section_notion')}
              description={tr('settings.section_notion_desc')}
            >
              {notion.configured ? (
                <div className="space-y-3" data-testid="notion-connected-state">
                  <div className="flex items-center gap-2 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                    <CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#10B981]">Conectado</p>
                      <p className="text-xs text-white/60 truncate">
                        Parent page · <span className="font-mono">{notion.parent_page_id}</span>
                      </p>
                      {notion.updated_at && (
                        <p className="text-[10px] text-white/40 mt-0.5">
                          Actualizado em {new Date(notion.updated_at).toLocaleString('pt-PT')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleNotionDisconnect}
                      data-testid="notion-disconnect-btn"
                      disabled={notionDisconnecting}
                      className="px-4 py-2 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 text-[#EF4444] rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                    >
                      <Plug size={14} weight="bold" className="inline mr-1.5" />
                      {notionDisconnecting ? tr('settings.disconnecting') : tr('settings.disconnect')}
                    </button>
                    <a
                      href="https://notion.so/my-integrations"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="notion-manage-link"
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1.5"
                    >
                      Gerir integrações <ArrowSquareOut size={12} weight="bold" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-xs text-white/60 leading-relaxed space-y-2">
                    <p className="font-medium text-white/80">Como configurar (1 minuto):</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>
                        Cria uma integração interna em{' '}
                        <a
                          href="https://notion.so/my-integrations"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#2563EB] hover:underline"
                        >
                          notion.so/my-integrations
                        </a>{' '}
                        e copia o <span className="font-mono">Internal Integration Token</span> (começa por <span className="font-mono">ntn_</span> ou <span className="font-mono">secret_</span>).
                      </li>
                      <li>Abre a página do Notion onde queres receber as exportações.</li>
                      <li>Clica em <span className="font-medium">&ldquo;…&rdquo; → Connections → Add connection</span> e selecciona a tua integração.</li>
                      <li>Copia o Page ID do URL (32 chars hex, depois do último <span className="font-mono">-</span>).</li>
                    </ol>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">Internal Integration Token</label>
                    <input
                      type="password"
                      data-testid="notion-token-input"
                      value={notionToken}
                      onChange={(e) => setNotionToken(e.target.value)}
                      placeholder="ntn_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">Parent Page ID</label>
                    <input
                      type="text"
                      data-testid="notion-page-id-input"
                      value={notionPageId}
                      onChange={(e) => setNotionPageId(e.target.value)}
                      placeholder="ex: 1f4d8c9e2a3b4d5f6g7h8i9j0k1l2m3n"
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <button
                    onClick={handleNotionSave}
                    data-testid="notion-save-btn"
                    disabled={notionSaving || !notionToken.trim() || !notionPageId.trim()}
                    className="px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg text-sm font-medium transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    <NotionLogo size={14} weight="fill" />
                    {notionSaving ? tr('settings.connecting') : `${tr('settings.connect_btn')} Notion`}
                  </button>
                </div>
              )}
            </SectionCard>
          </div>

          {/* Trello integration */}
          <div id="trello" data-testid="trello-section">
            <SectionCard
              icon={TrelloIcon}
              title={tr('settings.section_trello')}
              description={tr('settings.section_trello_desc')}
            >
              {trello.configured ? (
                <div className="space-y-3" data-testid="trello-connected-state">
                  <div className="flex items-center gap-2 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                    <CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#10B981]">Conectado</p>
                      <p className="text-xs text-white/60 truncate">
                        {trello.board_name ? <><span className="font-medium">{trello.board_name}</span> · </> : null}
                        {trello.list_name && <>lista <span className="font-medium">{trello.list_name}</span> · </>}
                        <span className="font-mono">{trello.default_list_id}</span>
                      </p>
                      {trello.updated_at && (
                        <p className="text-[10px] text-white/40 mt-0.5">
                          Actualizado em {new Date(trello.updated_at).toLocaleString('pt-PT')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleTrelloDisconnect}
                      data-testid="trello-disconnect-btn"
                      disabled={trelloDisconnecting}
                      className="px-4 py-2 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 text-[#EF4444] rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                    >
                      <Plug size={14} weight="bold" className="inline mr-1.5" />
                      {trelloDisconnecting ? tr('settings.disconnecting') : tr('settings.disconnect')}
                    </button>
                    <a
                      href="https://trello.com/power-ups/admin"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="trello-manage-link"
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1.5"
                    >
                      Gerir integrações <ArrowSquareOut size={12} weight="bold" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-xs text-white/60 leading-relaxed space-y-2">
                    <p className="font-medium text-white/80">Como configurar (2 minutos):</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>
                        Cria um Power-Up em{' '}
                        <a
                          href="https://trello.com/power-ups/admin"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#2563EB] hover:underline"
                        >
                          trello.com/power-ups/admin
                        </a>{' '}
                        e copia a <span className="font-mono">API Key</span>.
                      </li>
                      <li>
                        Gera um token autorizando o Power-Up em:{' '}
                        <code className="font-mono text-[#A855F7] break-all">https://trello.com/1/authorize?expiration=never&scope=read,write&response_type=token&name=FunnelHack&key={'<API_KEY>'}</code>
                      </li>
                      <li>Abre o board onde queres receber as exportações e copia o <span className="font-medium">List ID</span> de uma lista (URL do board → adicionar <span className="font-mono">.json</span> ao fim → procura <span className="font-mono">idList</span>).</li>
                    </ol>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">API Key</label>
                    <input
                      type="password"
                      data-testid="trello-key-input"
                      value={trelloApiKey}
                      onChange={(e) => setTrelloApiKey(e.target.value)}
                      placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">API Token</label>
                    <input
                      type="password"
                      data-testid="trello-token-input"
                      value={trelloApiToken}
                      onChange={(e) => setTrelloApiToken(e.target.value)}
                      placeholder="ATTA..."
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">Default List ID</label>
                    <input
                      type="text"
                      data-testid="trello-list-id-input"
                      value={trelloListId}
                      onChange={(e) => setTrelloListId(e.target.value)}
                      placeholder="ex: 6502abcdef0123456789abcd"
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <button
                    onClick={handleTrelloSave}
                    data-testid="trello-save-btn"
                    disabled={trelloSaving || !trelloApiKey.trim() || !trelloApiToken.trim() || !trelloListId.trim()}
                    className="px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg text-sm font-medium transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    <TrelloIcon size={14} weight="fill" />
                    {trelloSaving ? tr('settings.connecting') : `${tr('settings.connect_btn')} Trello`}
                  </button>
                </div>
              )}
            </SectionCard>
          </div>

          {/* ClickUp integration */}
          <div id="clickup" data-testid="clickup-section">
            <SectionCard
              icon={CheckSquare}
              title={tr('settings.section_clickup')}
              description={tr('settings.section_clickup_desc')}
            >
              {clickup.configured ? (
                <div className="space-y-3" data-testid="clickup-connected-state">
                  <div className="flex items-center gap-2 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                    <CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#10B981]">Conectado</p>
                      <p className="text-xs text-white/60 truncate">
                        {clickup.workspace_name && <><span className="font-medium">{clickup.workspace_name}</span> · </>}
                        {clickup.list_name && <>lista <span className="font-medium">{clickup.list_name}</span> · </>}
                        <span className="font-mono">{clickup.default_list_id}</span>
                      </p>
                      {clickup.updated_at && (
                        <p className="text-[10px] text-white/40 mt-0.5">
                          Actualizado em {new Date(clickup.updated_at).toLocaleString('pt-PT')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleClickupDisconnect}
                      data-testid="clickup-disconnect-btn"
                      disabled={clickupDisconnecting}
                      className="px-4 py-2 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 text-[#EF4444] rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                    >
                      <Plug size={14} weight="bold" className="inline mr-1.5" />
                      {clickupDisconnecting ? tr('settings.disconnecting') : tr('settings.disconnect')}
                    </button>
                    <a
                      href="https://app.clickup.com/settings/apps"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="clickup-manage-link"
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1.5"
                    >
                      Gerir apps <ArrowSquareOut size={12} weight="bold" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-xs text-white/60 leading-relaxed space-y-2">
                    <p className="font-medium text-white/80">Como configurar (1 minuto):</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>
                        Abre{' '}
                        <a
                          href="https://app.clickup.com/settings/apps"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#2563EB] hover:underline"
                        >
                          app.clickup.com/settings/apps
                        </a>{' '}
                        e gera um <span className="font-mono">Personal API Token</span> (começa por <span className="font-mono">pk_</span>).
                      </li>
                      <li>Abre a lista do ClickUp onde queres receber as exportações.</li>
                      <li>Copia o <span className="font-medium">List ID</span> do URL da lista (a parte numérica depois de <span className="font-mono">/li/</span>).</li>
                    </ol>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">Personal API Token</label>
                    <input
                      type="password"
                      data-testid="clickup-token-input"
                      value={clickupToken}
                      onChange={(e) => setClickupToken(e.target.value)}
                      placeholder="pk_xxxxxxx_XXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-[0.2em] text-white/50">Default List ID</label>
                    <input
                      type="text"
                      data-testid="clickup-list-id-input"
                      value={clickupListId}
                      onChange={(e) => setClickupListId(e.target.value)}
                      placeholder="ex: 901234567"
                      className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                  <button
                    onClick={handleClickupSave}
                    data-testid="clickup-save-btn"
                    disabled={clickupSaving || !clickupToken.trim() || !clickupListId.trim()}
                    className="px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg text-sm font-medium transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    <CheckSquare size={14} weight="fill" />
                    {clickupSaving ? tr('settings.connecting') : `${tr('settings.connect_btn')} ClickUp`}
                  </button>
                </div>
              )}
            </SectionCard>
          </div>

          {/* GDPR/LGPD */}
          <SectionCard
            icon={DownloadSimple}
            title={tr('settings.section_gdpr')}
            description={tr('settings.section_gdpr_desc')}
          >
            <button
              onClick={handleExport}
              data-testid="export-data-btn"
              disabled={exportLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
            >
              <DownloadSimple size={16} weight="bold" />
              {exportLoading ? tr('settings.exporting_data') : tr('settings.export_data')}
            </button>
          </SectionCard>

          {/* Danger zone */}
          <SectionCard icon={Warning} title={tr('settings.section_danger')} description={tr('settings.section_danger_desc')}>
            <p className="text-sm text-white/60 mb-3">
              {tr('settings.delete_account_warning')}
            </p>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                data-testid="delete-confirm-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={tr('settings.delete_account_confirm')}
                className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#EF4444]"
              />
              <button
                onClick={handleDeleteAccount}
                data-testid="delete-account-btn"
                disabled={!['APAGAR', 'ELIMINAR', 'DELETE'].includes(deleteConfirm.trim().toUpperCase()) || deleting}
                className="flex items-center gap-2 px-4 py-2 bg-[#EF4444]/20 hover:bg-[#EF4444]/30 text-[#EF4444] rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed self-start"
              >
                <Trash size={16} weight="bold" />
                {deleting ? tr('common.loading') : tr('settings.delete_account')}
              </button>
            </div>
          </SectionCard>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
